import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Modal, StatusBadge, useToast } from '@kss/ui';
import { toAbsoluteImageUrl } from '../utils/imageUrl';
import {
  enumLabel,
  invoiceItemService,
  invoiceService,
  orderReturnService,
  paymentService,
  publicCatalogService,
  salesOrderItemService,
  salesOrderService,
  type Invoice,
  type InvoiceItem,
  type Payment,
  type Product,
  type SalesOrder,
  type SalesOrderItem,
} from '@kss/api';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Download,
  MapPin,
  Package,
  Receipt,
  RotateCcw,
  Truck,
} from 'lucide-react';

/**
 * Customer-facing order details page.
 *
 * Pulls the SalesOrder header + line items + Invoice + Payment in
 * parallel from the live API. The detail page intentionally builds
 * its own picture of the order rather than trusting `SalesOrder/Get`
 * to return everything joined — the live endpoint returns
 * `items: []` even for orders that have rows in `SalesOrderItem`,
 * so we hit the line-item / invoice / payment services directly.
 */

interface ParsedAddress {
  fullName?: string;
  phone?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  pin?: string;
  country?: string;
}

function parseAddress(raw?: string | null): ParsedAddress | null {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as ParsedAddress;
  } catch {
    // not JSON — show raw text
  }
  return null;
}

function statusLabel(s?: number | string): string {
  const n = Number(s ?? 1);
  if (n === 5) return 'Cancelled';
  if (n === 4) return 'Delivered';
  if (n === 3) return 'Shipped';
  if (n === 2) return 'Confirmed';
  return 'Pending';
}

/**
 * Resolve a Payment.mode int to its human label using the canonical
 * `PaymentMode` enum registry from `@kss/api`. Same source of truth
 * used on the panel side (`AllPayments.tsx`), so labels never drift.
 */
function paymentModeLabel(mode: number | string | null | undefined): string {
  if (mode == null) return '—';
  const n = Number(mode);
  return Number.isFinite(n) ? enumLabel('PaymentMode', n) : String(mode);
}

export default function AccountOrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [order, setOrder] = useState<SalesOrder | null>(null);
  const [items, setItems] = useState<SalesOrderItem[]>([]);
  const [productById, setProductById] = useState<Record<string, Product>>({});
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  /* Return-request modal — opens when the customer clicks
     "Request return". POSTs to /api/OrderReturn on submit. Bug ref:
     AI Agent 2026-06-11 — "Return Order option missing on frontend". */
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnAmount, setReturnAmount] = useState<number>(0);
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    setLoading(true);
    setNotFound(false);

    (async () => {
      try {
        // Header — if this 404s the order isn't ours / doesn't exist.
        let header: SalesOrder | null = null;
        try {
          header = await salesOrderService.get(id);
        } catch {
          // fall back to a filter (some backends 404 on /Get for
          // missing rows, others throw — the filter never throws).
          const matches = await salesOrderService.filter({ salesOrderId: id });
          header = (matches ?? [])[0] ?? null;
        }
        if (!header) {
          if (!cancelled) {
            setNotFound(true);
          }
          return;
        }

        // Items, invoice, products in parallel.
        const [ordItems, invList] = await Promise.all([
          salesOrderItemService.filter({ salesOrderId: id }).catch(() => [] as SalesOrderItem[]),
          invoiceService.filter({ salesOrderId: id }).catch(() => [] as Invoice[]),
        ]);

        const inv = (invList ?? [])[0] ?? null;
        const [invItems, pays] = await Promise.all([
          inv?.invoiceId
            ? invoiceItemService
                .filter({ invoiceId: inv.invoiceId })
                .catch(() => [] as InvoiceItem[])
            : Promise.resolve([] as InvoiceItem[]),
          inv?.invoiceId
            ? paymentService
                .filter({ invoiceId: inv.invoiceId })
                .catch(() => [] as Payment[])
            : Promise.resolve([] as Payment[]),
        ]);

        // Product lookup so the line-item table can show names + thumbnails.
        // Public read works without auth — same data the storefront uses.
        const productIds = Array.from(
          new Set((ordItems ?? []).map((r) => r.productId).filter(Boolean) as string[]),
        );
        const products = await Promise.all(
          productIds.map((pid) => publicCatalogService.productById(pid).catch(() => null)),
        );
        const idx: Record<string, Product> = {};
        for (const p of products) {
          if (p?.productId) idx[p.productId] = p;
        }

        if (cancelled) return;
        setOrder(header);
        setItems(ordItems ?? []);
        setProductById(idx);
        setInvoice(inv);
        setInvoiceItems(invItems ?? []);
        setPayments(pays ?? []);
      } catch (e) {
        if (cancelled) return;
        toast.error(
          'Couldn’t load order',
          e instanceof Error ? e.message : 'Try again later.',
        );
        setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="grid gap-3">
        <div className="h-7 w-1/3 animate-pulse rounded bg-surface-card-hover" />
        <div className="h-32 animate-pulse rounded bg-surface-card-hover" />
        <div className="h-48 animate-pulse rounded bg-surface-card-hover" />
      </div>
    );
  }

  if (notFound || !order) {
    return (
      <div className="rounded-lg border border-border bg-surface-card p-10 text-center">
        <Package className="mx-auto h-8 w-8 text-content-muted" />
        <h1 className="mt-3 text-base font-semibold">Order not found</h1>
        <p className="mt-1 text-sm text-content-secondary">
          We couldn’t find that order on this account.
        </p>
        <Link to="/account/orders" className="mt-4 inline-block">
          <Button variant="outline" size="sm">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to orders
          </Button>
        </Link>
      </div>
    );
  }

  // Capture a non-null local so the closures below don't have to
  // re-narrow `order` (TS doesn't carry the early-return narrowing
  // into nested functions).
  const o: SalesOrder = order;
  const addr = parseAddress(o.shippingAddressJson);
  const total = Number(o.totalAmount ?? 0);
  const paidAmount = Number(invoice?.paidAmount ?? 0);
  const balance = Number(invoice?.balanceAmount ?? total);

  /* Returns are only sensible on orders that have actually shipped
     or been delivered (status enum 3 or 4). A "Cancelled" or
     "Pending" order should just be cancelled outright — that's a
     separate flow. */
  const statusInt = Number(o.status ?? 0);
  const returnEligible = statusInt === 3 || statusInt === 4;

  function openReturnModal() {
    setReturnReason('');
    setReturnAmount(total);
    setReturnOpen(true);
  }

  async function submitReturn() {
    if (!o.salesOrderId) return;
    if (!returnReason.trim()) {
      toast.error('Reason required', 'Tell us briefly why you want to return this order.');
      return;
    }
    setReturnSubmitting(true);
    try {
      await orderReturnService.create([{
        /* `orderType` 1 = SalesOrder. The header carries the source
           id via `orderId`. Status starts as `Requested` so the
           panel admin can review and approve/reject. */
        orderId: o.salesOrderId,
        orderType: 1,
        returnNumber: `RET-${Date.now()}`,
        returnDate: new Date().toISOString(),
        reason: returnReason.trim(),
        status: 'Requested',
        refundAmount: returnAmount,
        totalAmount: returnAmount,
        isActive: true,
      }]);
      toast.success(
        'Return requested',
        'Our team will review your return shortly. You will get an email when it is approved.',
      );
      setReturnOpen(false);
    } catch (e) {
      toast.error('Return failed', e instanceof Error ? e.message : 'Try again later.');
    } finally {
      setReturnSubmitting(false);
    }
  }

  /* SSO ROUND-2 ISSUE 4 — the legacy `downloadInvoice()` used to open
     a `window.open('')` popup and inject a bare inline HTML table
     (no company header, no GST, no signature, no "TAX INVOICE"
     branding). Customers saw that popup and reported the invoice
     download as "still wrong". The one canonical rendering path is
     now `/account/invoices/:invoiceId` — the same shared
     `InvoiceDocument` component the panel's
     `/panel/invoices/:id/preview` uses, exported via
     `useExport().exportToPdf` (html2canvas + jsPDF). The buttons on
     the Invoice card below just navigate there — the detail page
     auto-invokes the export when it sees `?action=download`. */

  return (
    <div className="grid gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Link
            to="/account/orders"
            className="inline-flex items-center gap-1 text-xs text-content-secondary hover:text-content-primary"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All orders
          </Link>
          <h1 className="mt-1 text-lg font-semibold">{o.orderNumber ?? id}</h1>
          <p className="text-xs text-content-secondary">
            Placed {(o.orderDate ?? '').slice(0, 10) || '—'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {/* Pass the human-readable label directly — StatusBadge's
              style map handles every order-state label (pending /
              confirmed / shipped / delivered / cancelled), so the
              token + duplicate caption pair we used to render is
              redundant and just confused users. */}
          <StatusBadge status={statusLabel(o.status)} />
          {/* Request return CTA — only shown for shipped/delivered
              orders (status enum 3 or 4). Earlier states should be
              cancelled outright, not returned. */}
          {returnEligible && (
            <Button variant="outline" size="sm" onClick={openReturnModal}>
              <RotateCcw className="h-3.5 w-3.5" /> Request return
            </Button>
          )}
        </div>
      </div>

      {!invoice && (
        <Alert variant="warning">
          Order saved, but no invoice was generated yet — our team will follow up shortly.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="grid gap-4">
          <Card className="p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <Package className="h-4 w-4" /> Items ({items.length})
            </div>
            {items.length === 0 ? (
              <p className="text-sm text-content-muted">No items recorded for this order.</p>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((line) => {
                  const product = productById[line.productId];
                  /* Thumbnail + name both link to the product detail
                     page so customers can re-order or read more from
                     the order history. `line.productId` is the raw
                     GUID; `decodeId` on `/shop/:id` accepts that
                     directly. Bug ref: AI Agent 2026-05-26 — "when
                     click cart product link then product view details
                     should be showing still not showing". */
                  const productHref = line.productId ? `/shop/${line.productId}` : null;
                  return (
                    <li
                      key={line.salesOrderItemId ?? `${line.productId}-${line.quantity}`}
                      className="flex items-center gap-3 py-3"
                    >
                      {productHref ? (
                        <Link
                          to={productHref}
                          className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-surface-card-hover"
                        >
                          {product?.imageUrl ? (
                            <img
                              src={toAbsoluteImageUrl(product.imageUrl)}
                              alt={product.name}
                              onError={(e) => {
                                /* Hide broken-image icon — fall back to
                                   the parent's bg + emoji glyph. */
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                              }}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-2xl opacity-60">
                              📦
                            </div>
                          )}
                        </Link>
                      ) : (
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-surface-card-hover text-2xl opacity-60">
                          📦
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        {productHref ? (
                          <Link
                            to={productHref}
                            className="block truncate text-sm font-medium hover:text-primary"
                          >
                            {product?.name ?? line.productId}
                          </Link>
                        ) : (
                          <div className="truncate text-sm font-medium">
                            {product?.name ?? line.productId}
                          </div>
                        )}
                        <div className="text-xs text-content-muted">
                          Qty {Number(line.quantity)} · ₹
                          {Number(line.unitPrice).toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div className="shrink-0 text-sm font-semibold">
                        ₹{Number(line.total ?? 0).toLocaleString('en-IN')}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className="p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <MapPin className="h-4 w-4" /> Shipping address
            </div>
            {addr ? (
              <div className="text-sm leading-relaxed text-content-secondary">
                <div className="font-medium text-content-primary">{addr.fullName}</div>
                <div>{addr.line1}</div>
                {addr.line2 && <div>{addr.line2}</div>}
                <div>
                  {addr.city}
                  {addr.state ? `, ${addr.state}` : ''} {addr.pin}
                </div>
                {addr.country && <div>{addr.country}</div>}
                {addr.phone && (
                  <div className="mt-1 text-content-muted">📱 {addr.phone}</div>
                )}
              </div>
            ) : (
              <p className="text-sm text-content-muted">
                {o.shippingAddressJson ?? 'No address on file.'}
              </p>
            )}
          </Card>

          {invoice && (
            <Card className="p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Receipt className="h-4 w-4" /> Invoice {invoice.invoiceNumber}
              </div>
              <div className="grid gap-1 text-xs text-content-secondary">
                <div>Issued {(invoice.invoiceDate ?? '').slice(0, 10) || '—'}</div>
                <div>
                  Status:{' '}
                  <span className="font-medium text-content-primary">
                    {Number(invoice.status) === 3
                      ? 'Paid'
                      : Number(invoice.status) === 2
                      ? 'Partial'
                      : 'Pending'}
                  </span>
                </div>
                <div>
                  Paid ₹{paidAmount.toLocaleString('en-IN')} · Balance ₹
                  {balance.toLocaleString('en-IN')}
                </div>
              </div>
              {/* SSO ROUND-2 ISSUE 4 — both actions route to the
                  shared print-ready layout at /account/invoices/:id
                  (same `InvoiceDocument` the panel renders). The
                  Download button just adds `?action=download` so the
                  detail page auto-invokes the PDF export the moment
                  the invoice is loaded. One canonical rendering path,
                  no bare-HTML popup. */}
              <div className="mt-3 flex flex-wrap gap-2">
                {invoice.invoiceId && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/account/invoices/${invoice.invoiceId}`)}
                    >
                      <Receipt className="h-3.5 w-3.5" /> View invoice
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(
                          `/account/invoices/${invoice.invoiceId}?action=download`,
                        )
                      }
                    >
                      <Download className="h-3.5 w-3.5" /> Download PDF
                    </Button>
                  </>
                )}
              </div>
            </Card>
          )}

          {payments.length > 0 && (
            <Card className="p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <CheckCircle2 className="h-4 w-4" /> Payments ({payments.length})
              </div>
              <ul className="divide-y divide-border text-sm">
                {payments.map((p) => (
                  <li key={p.paymentId} className="flex items-center justify-between py-2">
                    <div>
                      <div className="font-medium">{p.paymentNumber ?? p.paymentId}</div>
                      <div className="text-xs text-content-muted">
                        {(p.paymentDate ?? '').slice(0, 10)} · {paymentModeLabel(p.mode)}
                      </div>
                    </div>
                    <div className="font-semibold">₹{Number(p.amount).toLocaleString('en-IN')}</div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <aside className="h-fit rounded-lg border border-border bg-surface-card p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Truck className="h-4 w-4" /> Summary
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-content-secondary">Subtotal</span>
              <span>₹{Number(o.subTotal ?? 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-content-secondary">Tax</span>
              <span>₹{Number(o.taxAmount ?? 0).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-content-secondary">Shipping</span>
              <span>₹{Number(o.shippingAmount ?? 0).toLocaleString('en-IN')}</span>
            </div>
            {Number(o.discountAmount ?? 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-content-secondary">Discount</span>
                <span>−₹{Number(o.discountAmount).toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="my-2 border-t border-border" />
            <div className="flex justify-between text-base font-bold">
              <span>Total</span>
              <span>₹{total.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-md bg-surface-card-hover p-2 text-xs">
            <Clock className="h-3.5 w-3.5 text-content-muted" />
            <span className="text-content-secondary">{o.notes ?? 'No notes'}</span>
          </div>
          <Button
            variant="outline"
            fullWidth
            className="mt-4"
            onClick={() => navigate('/account/orders')}
          >
            Back to orders
          </Button>
        </aside>
      </div>

      {/* Return-request modal */}
      <Modal
        open={returnOpen}
        onClose={() => (returnSubmitting ? undefined : setReturnOpen(false))}
        title={`Return order ${o.orderNumber ?? ''}`}
        width={460}
        footer={
          <div className="flex w-full items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setReturnOpen(false)} disabled={returnSubmitting}>
              Cancel
            </Button>
            <Button onClick={submitReturn} disabled={returnSubmitting || !returnReason.trim()}>
              {returnSubmitting ? 'Submitting…' : 'Submit return request'}
            </Button>
          </div>
        }
      >
        <div className="grid gap-3">
          <p className="text-sm text-content-secondary">
            Tell us briefly why you want to return this order. Our team will review
            and contact you within 1–2 business days.
          </p>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Reason</span>
            <textarea
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              rows={4}
              placeholder="e.g. Wrong size, damaged on arrival, not as described…"
              maxLength={500}
              className="rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary"
              autoFocus
            />
            <span className="text-[11px] text-content-muted">{returnReason.length} / 500</span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-content-secondary">Refund amount (₹)</span>
            <input
              type="number"
              min={0}
              max={total}
              value={returnAmount}
              onChange={(e) => setReturnAmount(Math.max(0, Math.min(total, Number(e.target.value) || 0)))}
              className="h-10 rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary"
            />
            <span className="text-[11px] text-content-muted">Maximum: ₹{total.toLocaleString('en-IN')} (order total).</span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
